import React from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserCog, LogOut } from "lucide-react";

/**
 * Shows a bar at the top of the page when the current session is an admin
 * impersonating another user. Detects via the `admin_access_token` cookie
 * which is set only during impersonation.
 */
export default function ImpersonationBanner() {
  const { user, stopImpersonation } = useAuth();
  const nav = useNavigate();
  // Cookie check: admin_access_token is non-HttpOnly? No - it's HttpOnly, so JS cannot read it.
  // Fallback: show banner when the current user is not admin AND localStorage flag is set.
  const [active, setActive] = React.useState(false);
  React.useEffect(() => {
    try {
      setActive(localStorage.getItem("csi_impersonating") === "1");
    } catch { setActive(false); }
  }, [user]);

  if (!active || !user) return null;

  const stop = async () => {
    try {
      await stopImpersonation();
      try { localStorage.removeItem("csi_impersonating"); } catch {}
      toast.success("Returned to admin");
      nav("/admin");
    } catch (e) {
      toast.error("Could not return to admin - please log in again.");
    }
  };

  return (
    <div className="bg-[var(--terracotta)] text-white px-4 py-2 text-sm flex items-center justify-between gap-3 flex-wrap" data-testid="impersonation-banner">
      <div className="inline-flex items-center gap-2">
        <UserCog size={16}/>
        You are impersonating <b className="ml-1">{user.name} · {user.email}</b> ({user.role})
      </div>
      <button
        onClick={stop}
        className="inline-flex items-center gap-1 bg-white text-[var(--terracotta)] px-3 py-1 rounded-full text-xs font-bold hover:bg-black/5"
        data-testid="stop-impersonation-btn"
      >
        <LogOut size={12}/> Return to admin
      </button>
    </div>
  );
}
